import { ProjectSightmapSchema, type ProjectSightmap } from '../schemas/projectSightmap'
import { ProjectAccessSchema, AccessSuccessSchema, type ProjectAccess } from '../schemas/projectAccess'
import { PullRequestChecksSchema, type PullRequestChecks } from '../schemas/pullRequestChecks'
import { TOOL_CATALOG, type ToolCatalog } from '../guidance/app-tools/schema'
import { ARTIFACT_BUNDLE, type AppArtifactBundle } from '../guidance/app-tools/artifact-schema'
import { PlaywrightAnalysisSchema, PlaywrightAnalysesSchema, type PlaywrightAnalyses } from '../schemas/playwrightAnalyses'
import { SessionScreenshotResponseSchema, type SessionScreenshotResponse } from '../schemas/sessionScreenshot'
import { CreditUsageDaySchema, type CreditUsageDay, type CreditUsageDayInput, DailyCreditUsageSchema, type DailyCreditUsage, type CreditUsageInput } from '../schemas/creditUsage'
import { loadSessionDetail } from './loadSessionDetail'
import { securityOwnershipStatusSchema, type SecurityOwnershipStatus } from '../schemas/securityOwnership'
import { nonceCheckResultSchema, type NonceCheckResult } from '../lib/securityNonce'
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'
import { useCallback } from 'react'
import type { z } from 'zod'
import { apiFetchForQuery } from '../lib/apiFetch'
import { apiCacheBust } from '../lib/apiCacheBust'
import { groupPlansByTier, normalizeBackendPlan, type BackendPlan, type PlanTierGroup } from '../lib/stripe-config'
import { shouldRetryUnauthorizedQuery } from '../lib/projectQueryError'
import type { PullRequest, PullRequestDetail } from '../data/projectTypes'
import type {
  AppNotification,
  Bug,
  BugBreakdownRow,
  Comment,
  CommentTargetType,
  Exploration,
  GithubAppStatus,
  GithubAppInstallation,
  BugCounts,
  JourneyBug,
  Journey,
  PaginatedData,
  Project,
  ProjectInsightCounts,
  ProjectMember,
  ProjectTaskActivity,
  ProjectSecurityResults,
  ReverseProxyStatusResponse,
  ReverseProxyEvent,
  Task,
  TaskHistory,
  Team,
  TeamInviteInfo,
  TeamMember,
  TeamRole,
  TestRun,
  TestRunNotice,
  TestSession,
  MonitorInfo,
  Version,
} from '../data/projectTypes'
import { EXPERIMENTAL_DEFAULTS, type ActiveSubscription, type ApiTokenInfo, type GeneratedApiToken, type ProjectUsage, type SubscriptionResponse, type UsageData, type UserSettings, type UserSettingsPatch } from '../data/accountTypes'
import type { ChatMessageData } from '../components/chat/chatModel'
import type { TestRunOrderItem } from '../lib/githubTestRun'
import {
  ChatMessagesResponseSchema,
  BugCountsSchema,
  ExplorationSchema,
  JourneySchema,
  PaginatedExplorationsResponseSchema,
  PaginatedJourneysResponseSchema,
  ReorderJourneyQueueResponseSchema,
  BulkJourneyQueueResponseSchema,
  PaginatedTestRunsResponseSchema,
  InviteInfoSchema,
  ProjectMembersResponseSchema,
  TeamsResponseSchema,
  TeamMembersResponseSchema,
  TeamInviteInfoSchema,
  TeamInviteUrlResponseSchema,
  CommentSchema,
  CommentsResponseSchema,
  NotificationsResponseSchema,
  ProjectSchema,
  TestRunSchema,
  TestRunNoticesResponseSchema,
  TestSessionsPageResponseSchema,
  TestSessionResponseSchema,
  BugBreakdownSchema,
  BugSchema,
  GithubPullRequestDetailResponseSchema,
  GithubAppStatusSchema,
  GithubAppInstallationSchema,
  IssueTrackerTestResponseSchema,
  ProjectActionResponseSchema,
  JourneyBugsResponseSchema,
  PaginatedBugsResponseSchema,
  PaginatedProjectsResponseSchema,
  ProjectInsightsResponseSchema,
  ProcessingSummarySchema,
  ProjectTaskActivitySchema,
  ReverseProxyStatusSchema,
  ReverseProxyEventsResponseSchema,
  TaskHistorySchema,
  PaginatedVersionsResponseSchema,
  VersionSchema,
  CreateProjectUserSessionJourneyResponseSchema,
  ProjectUserSessionsPresenceResponseSchema,
  ProjectUserSessionsResponseSchema,
  type ProjectUserSessionSchema,
  ProjectUserSessionDetailResponseSchema,
  ProjectEnvironmentsResponseSchema,
  DeleteProjectEnvironmentResponseSchema,
  type ProjectEnvironmentSchema,
  ProjectEnvironmentMutationResponseSchema,
  ProjectSecurityResultsSchema,
} from '../schemas/projectListSchemas'
import {
  ApiTokensResponseSchema,
  CreditPacksResponseSchema,
  CreditTopUpResponseSchema,
  GeneratedApiTokenSchema,
  ProjectUsageSchema,
  UserSettingsResponseSchema,
  UsageDataSchema,
  SubscriptionResponseSchema,
  type CreditPacksResponse,
} from '../schemas/accountSchemas'

export const PROJECT_LIST_PAGE_SIZE = 50

export interface ProjectTestSessionsPage {
  items: TestSession[]
  total: number
  page: number
  pageSize: number
  testRunOrder: TestRunOrderItem[]
  /** Collapsed-row summaries for the GitHub PRs on this page — the list needs no separate PR fetch. */
  githubPrs: PullRequest[]
}

export interface ProjectChatMessage extends ChatMessageData {
  source?: string | null
}

export type ProjectUserSession = z.infer<typeof ProjectUserSessionSchema>

export type ProjectUserSessionDetail = z.infer<typeof ProjectUserSessionDetailResponseSchema>

export type ProjectEnvironment = z.infer<typeof ProjectEnvironmentSchema>

export type InviteInfo = z.infer<typeof InviteInfoSchema>

function parsePaginatedJourneys(response: unknown): PaginatedData<Journey> {
  const data = PaginatedJourneysResponseSchema.parse(response)
  return {
    ...data,
    // The list endpoint intentionally returns version metadata only. Preserve the shared Journey
    // shape for list consumers while making the missing opaque payload explicit; the detail query
    // remains the source of truth for full version steps.
    items: data.items.map((journey) => ({
      ...journey,
      versions: journey.versions.map((version) => ({ ...version, steps: null })),
    })),
  }
}

function parseUserSettings(response: unknown): UserSettings {
  const data = UserSettingsResponseSchema.parse(response)
  return {
    notify_on_project_completion: data.notify_on_project_completion ?? true,
    experimental_settings: { ...EXPERIMENTAL_DEFAULTS, ...(data.experimental_settings ?? {}) },
    // Absent (an older deploy answering) means don't welcome anyone — better to miss a dialog than to
    // show every existing user one.
    welcome_pending: data.welcome_pending === true,
  }
}

function nextPage<T extends { page: number; pageSize: number; total: number }>(lastPage: T): number | undefined {
  return lastPage.page * lastPage.pageSize < lastPage.total ? lastPage.page + 1 : undefined
}

function bugProjectTags(projectId: string) {
  return [
    { type: 'Project' as const, id: `bugs:${projectId}` },
    { type: 'Project' as const, id: `bug-counts:${projectId}` },
    { type: 'Project' as const, id: `bug-breakdown:${projectId}` },
    { type: 'Project' as const, id: `security:${projectId}` },
  ]
}

function bugMutationTags(result: Bug | undefined, bugId: string) {
  return [
    { type: 'Project' as const, id: `bug:${bugId}` },
    ...(result?.project_id ? bugProjectTags(result.project_id) : []),
  ]
}

// apiFetchForQuery supplies the app's auth-expiry behavior and the dev fixture adapter, and keeps a
// 401 on the response so endpoint errors carry `status: 401` (see isUnauthorizedError). Endpoint
// definitions still use RTK Query's normal fetchBaseQuery request/params/body handling.
//
// retry covers the spurious-401 case (bug-muh11lve-wv7a): a burst of project-scoped requests 401'd
// while the session was alive — /api/auth-me and sibling requests confirmed it — dead-ending the
// project page on the "Login required" gate. A 401 that contradicts the auth state's verified
// signed-in user is retried once; a signed-out visitor's 401 (which the sign-in redirect owns) and
// every other failure are left to the existing error handling.
const baseQuery = retry(
  fetchBaseQuery({
    baseUrl: '/',
    fetchFn: (input, init) => apiFetchForQuery(input, init),
  }),
  {
    // RTK 2.5's RetryOptions is a discriminated union: retryCondition and maxRetries are mutually
    // exclusive, so the retry-once cap lives in the condition — `attempt` is 1-based on the first
    // failure, so `attempt <= 1` fires exactly one retry. The api arrives as baseQueryApi on the
    // third argument (the second is the query args).
    retryCondition: (error, _args, { attempt, baseQueryApi }) => {
      const auth = (baseQueryApi.getState() as { auth?: { user?: unknown } }).auth
      return attempt <= 1 && shouldRetryUnauthorizedQuery(error, auth?.user)
    },
  },
)

// Seed a freshly-created project into an already-cached project-list page so subscribed lists (the
// AppShell sidebar among them) reflect it immediately, without invalidating the LIST tag and firing a
// GET /api/projects refetch behind the create — the round-trip that #3995 flagged on the onboarding
// waterfall. The server orders lists updated_at DESC, so a brand-new project belongs at the front; we
// cap the page back to its own pageSize (the trailing row spills to the next page, exactly as it would
// with the server's OFFSET pagination) and bump the totals. A project already present (a retried
// create, or a list that raced a refetch) is a no-op so nothing is duplicated or double-counted.
export function seedProjectIntoList(page: PaginatedData<Project>, created: Project): void {
  if (page.items.some((project) => project.id === created.id)) return
  page.items.unshift(created)
  if (page.items.length > page.pageSize) page.items.splice(page.pageSize)
  page.total += 1
  if (typeof page.totalAll === 'number') page.totalAll += 1
}

export const projectDataApi = createApi({
  reducerPath: 'projectDataApi',
  baseQuery,
  tagTypes: [
    'Project',
    'ProjectMembers',
    'Teams',
    'TeamMembers',
    'ProjectGithub',
    'ProjectVersions',
    'Version',
    'UserSettings',
    'Usage',
    'ApiToken',
    'ProjectJourneys',
    'ProjectExplorations',
    'ProjectTestSessions',
    'ProjectChat',
    'ProjectEnvironments',
    'ProjectAccess',
    'SecurityOwnership',
    'Journey',
    'JourneyBugs',
    'Exploration',
    'TestRun',
    'Comments',
    'Notifications',
    'ProjectUserSessions',
  ],
  keepUnusedDataFor: 5 * 60,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getProjectSightmap: builder.query<ProjectSightmap | null, string>({
      async queryFn(projectId, _api, _options, baseQuery) {
        const response = await baseQuery(`/api/projects/${encodeURIComponent(projectId)}/sightmap`)
        if (response.error) return response.error.status === 404 ? { data: null } : { error: response.error }
        const parsed = ProjectSightmapSchema.safeParse(response.data)
        return parsed.success ? { data: parsed.data } : { error: { status: 'CUSTOM_ERROR', error: 'Invalid site map response' } }
      },
    }),
    getAppTools: builder.query<ToolCatalog, string>({
      query: projectId => `/api/projects/${encodeURIComponent(projectId)}/app-tools`,
      transformResponse: (response: unknown) => TOOL_CATALOG.parse(response),
    }),
    getAppArtifacts: builder.query<AppArtifactBundle, { projectId: string; runId?: string }>({
      query: ({ projectId, runId }) => `/api/projects/${encodeURIComponent(projectId)}/app-artifacts${runId ? `?runId=${encodeURIComponent(runId)}` : ''}`,
      transformResponse: (response: unknown) => ARTIFACT_BUNDLE.parse(response),
      keepUnusedDataFor: 0,
    }),
    getProjects: builder.query<PaginatedData<Project>, { statusFilter?: string; page?: number; pageSize?: number } | void>({
      // Omitted defaults and explicit defaults describe the same request and must share its cache.
      serializeQueryArgs: ({ queryArgs }) => ({
        page: queryArgs?.page ?? 1,
        pageSize: queryArgs?.pageSize ?? 20,
        statusFilter: queryArgs?.statusFilter || 'all',
      }),
      query: (args) => ({
        url: '/api/projects',
        params: {
          page: args?.page ?? 1,
          pageSize: args?.pageSize ?? 20,
          ...(args?.statusFilter && args.statusFilter !== 'all' ? { status: args.statusFilter } : {}),
        },
      }),
      transformResponse: (response) => PaginatedProjectsResponseSchema.parse(response) as unknown as PaginatedData<Project>,
      providesTags: [{ type: 'Project', id: 'LIST' }],
    }),

    getProject: builder.query<Project, string>({
      query: (projectId) => ({ url: `/api/projects/${encodeURIComponent(projectId)}` }),
      transformResponse: (response) => ProjectSchema.parse(response) as unknown as Project,
      providesTags: (_result, _error, projectId) => [{ type: 'Project', id: projectId }],
    }),

    getProjectAccess: builder.query<ProjectAccess, string>({
      query: projectId => ({ url: '/api/project-access', params: { project_id: projectId } }),
      transformResponse: response => ProjectAccessSchema.parse(response),
      providesTags: (_result, _error, projectId) => [{ type: 'ProjectAccess', id: projectId }],
    }),

    answerAccessRequest: builder.mutation<{ ok: true }, { projectId: string; requestId: string; value: string }>({
      query: ({ projectId, requestId, value }) => ({ url: '/api/project-access', method: 'PUT', body: { action: 'answer', project_id: projectId, request_id: requestId, value } }),
      transformResponse: response => AccessSuccessSchema.parse(response),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'ProjectAccess', id: projectId }],
    }),

    getSecurityOwnership: builder.query<SecurityOwnershipStatus, { projectId: string; targetUrl: string; nonce: string }>({
      query: ({ projectId }) => ({ url: '/api/verify-security-nonce', params: { projectId } }),
      transformResponse: (response) => securityOwnershipStatusSchema.parse(response),
      providesTags: (_result, _error, { projectId }) => [{ type: 'SecurityOwnership', id: projectId }],
    }),

    verifyProjectOwnership: builder.mutation<NonceCheckResult, string>({
      query: (projectId) => ({ url: '/api/verify-security-nonce', method: 'POST', body: { projectId } }),
      transformResponse: (response) => nonceCheckResultSchema.parse(response),
      invalidatesTags: (_result, _error, projectId) => [{ type: 'SecurityOwnership', id: projectId }],
    }),

    getProjectEnvironments: builder.query<ProjectEnvironment[], string>({
      query: (projectId) => ({ url: '/api/project-environments', params: { project_id: projectId } }),
      transformResponse: (response) => ProjectEnvironmentsResponseSchema.parse(response).environments,
      providesTags: (_result, _error, projectId) => [{ type: 'ProjectEnvironments', id: projectId }],
    }),

    deleteProjectEnvironment: builder.mutation<{ success: true }, { projectId: string; environmentId: string }>({
      query: ({ projectId, environmentId }) => ({
        url: `/api/project-environments/${encodeURIComponent(environmentId)}`,
        method: 'DELETE',
        body: { project_id: projectId },
      }),
      transformResponse: (response) => DeleteProjectEnvironmentResponseSchema.parse(response),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'ProjectEnvironments', id: projectId }],
    }),

    updateProjectEnvironment: builder.mutation<ProjectEnvironment, {
      projectId: string
      environmentId: string
      environment: Pick<ProjectEnvironment, 'name' | 'kind' | 'target_url' | 'testing_guidelines' | 'environment_variables' | 'trigger_type' | 'trigger_config'>
    }>({
      query: ({ projectId, environmentId, environment }) => ({
        url: `/api/project-environments/${encodeURIComponent(environmentId)}`,
        method: 'PUT',
        body: { project_id: projectId, environment },
      }),
      transformResponse: (response) => {
        const parsed = ProjectEnvironmentMutationResponseSchema.parse(response)
        return { ...parsed.environment, excluded_journey_ids: parsed.excluded_journey_ids }
      },
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'ProjectEnvironments', id: projectId }],
    }),

    // Public, unauthenticated summary for the invite landing page (/invite/:projectId). Backed by the
    // /api/invite-info endpoint so a logged-out invitee can render the project's name + thumbnail
    // before signing in.
    getInviteInfo: builder.query<InviteInfo, string>({
      query: (projectId) => ({ url: '/api/invite-info', params: { projectId } }),
      transformResponse: (response) => InviteInfoSchema.parse(response),
      providesTags: (_result, _error, projectId) => [{ type: 'Project', id: `invite-info:${projectId}` }],
    }),

    updateProject: builder.mutation<Project, { id: string; data: Record<string, unknown> }>({
      query: ({ id, data }) => ({
        url: `/api/projects/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: data.action ? data : { action: 'update-settings', ...data },
      }),
      transformResponse: (response) => ProjectSchema.parse(response) as unknown as Project,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Project', id }],
    }),

    testIssueTracker: builder.mutation<{ success?: boolean; detail?: string; error?: string }, string>({
      query: (projectId) => ({
        url: `/api/projects/${encodeURIComponent(projectId)}`,
        method: 'PATCH',
        body: { action: 'test-issue-tracker' },
      }),
      transformResponse: (response) => IssueTrackerTestResponseSchema.parse(response),
    }),

    testSummaryDestination: builder.mutation<{ success?: boolean; detail?: string; error?: string }, { projectId: string; channel: 'slack' | 'discord' | 'email' }>({
      query: ({ projectId, channel }) => ({
        url: `/api/projects/${encodeURIComponent(projectId)}`,
        method: 'PATCH',
        body: { action: 'test-summary-destination', channel },
      }),
      transformResponse: (response) => IssueTrackerTestResponseSchema.parse(response),
    }),

    sendCompletionEmail: builder.mutation<{ success?: boolean; detail?: string; error?: string }, { projectId: string; recipient?: string }>({
      query: ({ projectId, recipient = 'owner' }) => ({
        url: `/api/projects/${encodeURIComponent(projectId)}`,
        method: 'PATCH',
        body: { action: 'send-completion-email', recipient },
      }),
      transformResponse: (response) => ProjectActionResponseSchema.parse(response),
    }),

    createProject: builder.mutation<Project, Record<string, unknown>>({
      query: (body) => ({ url: '/api/projects', method: 'POST', body }),
      transformResponse: (response) => ProjectSchema.parse(response) as unknown as Project,
      // The create response already embeds the new project, so seed the cached project lists
      // from it instead of invalidating the LIST tag: the invalidation made every subscribed
      // list (the AppShell sidebar among them) refetch GET /api/projects right behind the
      // create, chaining a full list round-trip onto the onboarding request waterfall (#3995),
      // and it fired even on a failed create, when nothing changed server-side.
      // updateQueryData only patches a list that is already cached — an uncached one fetches
      // fresh when first subscribed, so no entry is synthesized here.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled
          dispatch(
            projectDataApi.util.updateQueryData('getProjects', { pageSize: 50 }, (draft) => {
              seedProjectIntoList(draft, created)
            }),
          )
        } catch {
          // Mutation failed — nothing was created, nothing to seed. The failure still reaches
          // the surface through the mutation's own error state; nothing new is swallowed here.
        }
      },
    }),

    startProjectQa: builder.mutation<Project, { id: string; settings?: Record<string, unknown>; global_variables?: unknown; instructions?: string; chat_messages?: unknown }>({
      query: ({ id, ...body }) => ({ url: `/api/projects/${encodeURIComponent(id)}`, method: 'PATCH', body: { action: 'start-qa', ...body } }),
      transformResponse: (response) => ProjectSchema.parse(response) as unknown as Project,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Project', id }, { type: 'Project', id: 'LIST' }],
    }),

    deleteProject: builder.mutation<void, string>({
      query: (id) => ({ url: `/api/projects/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Project', id }, { type: 'Project', id: 'LIST' }],
    }),

    getReverseProxyStatus: builder.query<ReverseProxyStatusResponse, string>({
      query: (projectId) => ({ url: '/api/reverse-proxy', params: { project_id: projectId } }),
      transformResponse: (response) => ReverseProxyStatusSchema.parse(response) as unknown as ReverseProxyStatusResponse,
      providesTags: (_result, _error, projectId) => [{ type: 'Project', id: `reverse-proxy:${projectId}` }],
    }),

    getReverseProxyEvents: builder.query<{ enabled: boolean; events: ReverseProxyEvent[] }, string>({
      query: (projectId) => ({ url: '/api/reverse-proxy-events', params: { project_id: projectId } }),
      transformResponse: (response) => {
        const data = ReverseProxyEventsResponseSchema.parse(response)
        return { enabled: data.enabled ?? true, events: data.events as ReverseProxyEvent[] }
      },
      providesTags: (_result, _error, projectId) => [{ type: 'Project', id: `reverse-proxy-events:${projectId}` }],
    }),

    deleteReverseProxy: builder.mutation<void, string>({
      query: (projectId) => ({ url: '/api/reverse-proxy', method: 'DELETE', params: { project_id: projectId } }),
      invalidatesTags: (_result, _error, projectId) => [{ type: 'Project', id: `reverse-proxy:${projectId}` }],
    }),

    getProjectMembers: builder.query<{ items: ProjectMember[]; role: string }, string>({
      query: (projectId) => ({ url: '/api/project-members', params: { project_id: projectId } }),
      transformResponse: (response) => ProjectMembersResponseSchema.parse(response) as { items: ProjectMember[]; role: string },
      providesTags: (_result, _error, projectId) => [{ type: 'ProjectMembers', id: projectId }],
    }),

    getGithubAppStatus: builder.query<GithubAppStatus, { repo?: string; projectId?: string }>({
      query: ({ repo, projectId }) => ({
        url: '/api/github-app-status',
        params: repo ? { repo } : { project_id: projectId ?? '' },
      }),
      transformResponse: (response) => GithubAppStatusSchema.parse(response) as GithubAppStatus,
      providesTags: (_result, _error, { repo, projectId }) => [{ type: 'ProjectGithub', id: repo ? `repo:${repo}` : `project:${projectId ?? ''}` }],
    }),

    // One installation of the GitHub App. Reading it also CLAIMS the installation for the signed-in
    // user (the endpoint links the two, which is what makes it appear in Settings → GitHub), so the
    // post-install landing fetches it even when all it renders is the account name.
    getGithubAppInstallation: builder.query<GithubAppInstallation, string>({
      query: (installationId) => ({
        url: '/api/github-app-installation',
        params: { installation_id: installationId },
      }),
      transformResponse: (response) => GithubAppInstallationSchema.parse(response) as GithubAppInstallation,
      providesTags: (_result, _error, installationId) => [{ type: 'ProjectGithub', id: `installation:${installationId}` }],
    }),

    // The teams the user belongs to. Reading this also creates their own team the first time (see
    // ensurePersonalTeam), which is what adopts an existing account's projects onto the teams model —
    // so it is deliberately a plain read with a side effect the caller never has to think about.
    getTeams: builder.query<{ items: Team[] }, void>({
      query: () => ({ url: '/api/teams' }),
      transformResponse: (response) => TeamsResponseSchema.parse(response) as { items: Team[] },
      providesTags: [{ type: 'Teams', id: 'LIST' }],
    }),

    renameTeam: builder.mutation<{ id: string; name: string }, { teamId: string; name: string }>({
      query: ({ teamId, name }) => ({
        url: `/api/teams/${encodeURIComponent(teamId)}`,
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: [{ type: 'Teams', id: 'LIST' }],
    }),

    // Mint or revoke the shareable self-join link. Minting again is how a link is revoked-and-replaced,
    // so both actions live in one mutation.
    updateTeamInviteLink: builder.mutation<{ invite_url: string | null }, { teamId: string; action: 'regenerate-invite' | 'revoke-invite' }>({
      query: ({ teamId, action }) => ({
        url: `/api/teams/${encodeURIComponent(teamId)}`,
        method: 'PATCH',
        body: { action },
      }),
      transformResponse: (response) => TeamInviteUrlResponseSchema.parse(response) as { invite_url: string | null },
      invalidatesTags: [{ type: 'Teams', id: 'LIST' }],
    }),

    getTeamMembers: builder.query<{ items: TeamMember[]; role: TeamRole }, string>({
      query: (teamId) => ({ url: '/api/team-members', params: { team_id: teamId } }),
      transformResponse: (response) => TeamMembersResponseSchema.parse(response) as { items: TeamMember[]; role: TeamRole },
      providesTags: (_result, _error, teamId) => [{ type: 'TeamMembers', id: teamId }],
    }),

    inviteTeamMember: builder.mutation<TeamMember, { teamId: string; email: string; role?: TeamRole }>({
      query: ({ teamId, email, role }) => ({
        url: '/api/team-members',
        method: 'POST',
        body: { team_id: teamId, email, ...(role ? { role } : {}) },
      }),
      // The roster changes, and so does the member count on the team summary.
      invalidatesTags: (_result, _error, { teamId }) => [{ type: 'TeamMembers', id: teamId }, { type: 'Teams', id: 'LIST' }],
    }),

    updateTeamMemberRole: builder.mutation<TeamMember, { teamId: string; memberId: string; role: TeamRole }>({
      query: ({ memberId, role }) => ({
        url: `/api/team-members/${encodeURIComponent(memberId)}`,
        method: 'PATCH',
        body: { role },
      }),
      invalidatesTags: (_result, _error, { teamId }) => [{ type: 'TeamMembers', id: teamId }],
    }),

    // Removing someone, and leaving a team yourself, are the same request — the server decides which
    // it is from whose membership row the id names.
    removeTeamMember: builder.mutation<void, { teamId: string; memberId: string }>({
      query: ({ memberId }) => ({ url: `/api/team-members/${encodeURIComponent(memberId)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { teamId }) => [
        { type: 'TeamMembers', id: teamId },
        { type: 'Teams', id: 'LIST' },
        // Leaving a team drops every project it gave access to.
        { type: 'Project', id: 'LIST' },
      ],
    }),

    // Public, unauthenticated team summary for the /join/:token landing page, so a logged-out invitee
    // sees which team they were invited to before signing in.
    getTeamInviteInfo: builder.query<TeamInviteInfo, string>({
      query: (token) => ({ url: '/api/team-invite-info', params: { token } }),
      transformResponse: (response) => TeamInviteInfoSchema.parse(response) as TeamInviteInfo,
      providesTags: (_result, _error, token) => [{ type: 'Teams', id: `invite-info:${token}` }],
    }),

    joinTeamByInvite: builder.mutation<{ team: { id: string; name: string } }, string>({
      query: (token) => ({ url: '/api/team-members', method: 'POST', body: { invite_token: token } }),
      invalidatesTags: [{ type: 'Teams', id: 'LIST' }, { type: 'Project', id: 'LIST' }, { type: 'Usage', id: 'CURRENT' }],
    }),

    getPullRequestChecks: builder.query<PullRequestChecks, { projectId: string; repo: string; pr: number }>({
      query: ({ projectId, repo, pr }) => ({ url: '/api/github-pr-checks', params: { project_id: projectId, repo, pr } }),
      transformResponse: response => PullRequestChecksSchema.parse(response),
      providesTags: (_result, _error, { projectId }) => [{ type: 'ProjectGithub', id: projectId }],
    }),

    // The run detail page fetches the PR's journeys, screenshots, and polish passes on demand.
    getProjectPullRequest: builder.query<{ pr: PullRequestDetail }, { projectId: string; versionId: string }>({
      query: ({ projectId, versionId }) => ({
        url: '/api/github-prs',
        params: { project_id: projectId, version_id: versionId },
      }),
      transformResponse: (response) => {
        const data = GithubPullRequestDetailResponseSchema.parse(response)
        return { pr: data.pr as PullRequestDetail }
      },
      providesTags: (_result, _error, { projectId }) => [{ type: 'ProjectGithub', id: projectId }],
    }),

    getProjectVersions: builder.query<{ items: Version[]; total: number; page: number; pageSize: number; monitor: MonitorInfo }, { projectId: string; page?: number; pageSize?: number }>({
      query: ({ projectId, page = 1, pageSize = 20 }) => ({
        url: '/api/versions',
        params: { project_id: projectId, page, pageSize },
      }),
      transformResponse: (response) => {
        const data = PaginatedVersionsResponseSchema.parse(response)
        return {
          items: data.items as Version[],
          total: data.total,
          page: data.page,
          pageSize: data.pageSize,
          monitor: (data.monitor ?? { active: null, runs: [] }) as MonitorInfo,
        }
      },
      providesTags: (_result, _error, { projectId }) => [{ type: 'ProjectVersions', id: projectId }],
    }),

    getVersion: builder.query<Version, string>({
      query: (versionId) => ({ url: `/api/versions/${encodeURIComponent(versionId)}` }),
      transformResponse: (response) => VersionSchema.parse(response) as Version,
      providesTags: (_result, _error, versionId) => [{ type: 'Version', id: versionId }],
    }),

    // Credits belong to a team. Passing nothing lets the server answer for the best-funded team the
    // caller belongs to, which is what the credit gate wants (see useTeams); a team id asks about that
    // team specifically; 'own' asks about the caller's own subscription and ledger, ignoring teams they
    // were invited to. Daily usage also defaults to the caller's own account. All variants share a
    // cache tag, so a credit change refetches the current summaries and daily breakdowns together.
    getDailyCreditUsage: builder.query<DailyCreditUsage, CreditUsageInput>({
      query: (input) => ({ url: '/api/usage-daily', params: input }),
      transformResponse: (response) => DailyCreditUsageSchema.parse(response),
      providesTags: [{ type: 'Usage', id: 'CURRENT' }],
    }),
    getCreditUsageDay: builder.query<CreditUsageDay, CreditUsageDayInput>({
      query: (input) => ({ url: '/api/usage-day', params: input }),
      transformResponse: (response) => CreditUsageDaySchema.parse(response),
      providesTags: [{ type: 'Usage', id: 'CURRENT' }],
    }),
    getUsage: builder.query<UsageData, string | undefined>({
      query: (scope) => ({
        url: '/api/usage',
        ...(scope === 'own' ? { params: { scope: 'own' } } : scope ? { params: { team_id: scope } } : {}),
      }),
      transformResponse: (response) => UsageDataSchema.parse(response) as UsageData,
      providesTags: [{ type: 'Usage', id: 'CURRENT' }],
    }),

    // The one-off credit packs on sale. Catalog data that changes by hand in Stripe, so it is fetched
    // once and kept — the credit meter asks only when it has a top-up to offer.
    getCreditPacks: builder.query<CreditPacksResponse, void>({
      query: () => ({ url: '/api/stripe-credit-packs' }),
      transformResponse: (response) => CreditPacksResponseSchema.parse(response),
      providesTags: [{ type: 'Usage', id: 'PACKS' }],
    }),

    // Starts a credit pack purchase and hands back the Stripe Checkout URL for the caller to send the
    // buyer to. Grants nothing: credits arrive via the checkout.session.completed webhook, so this
    // invalidates no balance — the return trip refreshes it (see CreditMeter).
    startCreditTopUp: builder.mutation<{ url: string }, { priceId: string; returnPath: string }>({
      query: (body) => ({ url: '/api/stripe-credit-topup', method: 'POST', body }),
      transformResponse: (response) => CreditTopUpResponseSchema.parse(response),
    }),

    // The workspace subscription, read by every surface that gates on the plan (see useSubscription).
    // It lives here rather than in a bare useEffect fetch so those surfaces share one request instead
    // of each mount firing its own.
    getStripeSubscription: builder.query<SubscriptionResponse, void>({
      query: () => ({ url: '/api/stripe-subscription' }),
      transformResponse: (response) => {
        const data = SubscriptionResponseSchema.parse(response)
        return { subscription: data.subscription as ActiveSubscription | null, workspaceId: data.workspaceId ?? null }
      },
      providesTags: [{ type: 'Usage', id: 'SUBSCRIPTION' }],
    }),

    // The plan catalog is static per session but read from several surfaces (the settings tab and the
    // credit gate can be mounted together), so it goes through the shared cache. A per-component
    // fetch re-requested it once per mount (QA bug bug-mufwmqp1-shyw: 4 GETs within one session).
    getStripePlans: builder.query<PlanTierGroup[], void>({
      query: () => ({ url: '/api/stripe-plans' }),
      transformResponse: (response: { plans: BackendPlan[] }) =>
        groupPlansByTier(response.plans.map(normalizeBackendPlan)),
      providesTags: [{ type: 'Usage', id: 'STRIPE_PLANS' }],
    }),

    getUserSettings: builder.query<UserSettings, void>({
      query: () => ({ url: '/api/user-settings' }),
      transformResponse: (response) => parseUserSettings(response),
      providesTags: [{ type: 'UserSettings', id: 'CURRENT' }],
    }),

    updateUserSettings: builder.mutation<UserSettings, UserSettingsPatch>({
      query: (patch) => ({
        url: '/api/user-settings',
        method: 'PATCH',
        body: patch,
      }),
      transformResponse: (response) => parseUserSettings(response),
      invalidatesTags: [{ type: 'UserSettings', id: 'CURRENT' }],
    }),

    getProjectUsage: builder.query<{ project: ProjectUsage | null }, string>({
      query: (projectId) => ({ url: '/api/usage', params: { project_id: projectId } }),
      transformResponse: (response) => {
        const value = response && typeof response === 'object' && 'project' in response
          ? (response as { project?: unknown }).project
          : null
        return { project: value === null || value === undefined ? null : ProjectUsageSchema.parse(value) as ProjectUsage }
      },
      providesTags: (_result, _error, projectId) => [{ type: 'Usage', id: `project:${projectId}` }],
    }),

    getApiTokens: builder.query<{ tokens: ApiTokenInfo[] }, string>({
      query: () => ({ url: '/api/api-tokens' }),
      transformResponse: (response) => ApiTokensResponseSchema.parse(response),
      providesTags: (_result, _error, userId) => [{ type: 'ApiToken', id: userId }],
      keepUnusedDataFor: 0,
    }),

    generateApiToken: builder.mutation<GeneratedApiToken, { userId: string; name: string }>({
      query: ({ name }) => ({ url: '/api/api-tokens', method: 'POST', body: { name } }),
      transformResponse: (response) => GeneratedApiTokenSchema.parse(response),
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'ApiToken', id: userId }],
    }),

    deleteApiToken: builder.mutation<void, { userId: string; id: string }>({
      query: ({ id }) => ({ url: '/api/api-tokens', method: 'DELETE', body: { id } }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'ApiToken', id: userId }],
    }),

    getProjectTestSessions: builder.infiniteQuery<
      ProjectTestSessionsPage,
      { projectId: string; pageSize?: number },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => nextPage(lastPage),
        refetchCachedPages: false,
      },
      query: ({ queryArg, pageParam }) => ({
        url: '/api/test-sessions',
        params: {
          project_id: queryArg.projectId,
          page: pageParam,
          pageSize: queryArg.pageSize ?? PROJECT_LIST_PAGE_SIZE,
        },
      }),
      transformResponse: (response) => {
        const data = TestSessionsPageResponseSchema.parse(response)
        return {
          items: data.items,
          total: data.total,
          page: data.page,
          pageSize: data.pageSize,
          testRunOrder: data.test_run_order ?? [],
          githubPrs: (data.github_prs ?? []) as PullRequest[],
        }
      },
      providesTags: (_result, _error, arg) => [{ type: 'ProjectTestSessions', id: arg.projectId }],
    }),

    getProjectTestSession: builder.query<{ session: TestSession | null }, { projectId: string; sessionId?: string; versionId?: string }>({
      query: ({ projectId, sessionId, versionId }) => ({
        url: '/api/test-sessions',
        params: { project_id: projectId, ...(versionId ? { version_id: versionId } : { session_id: sessionId }) },
      }),
      transformResponse: (response: unknown) => TestSessionResponseSchema.parse(response),
      providesTags: (_result, _error, arg) => [{ type: 'ProjectTestSessions', id: arg.projectId }],
    }),

    // The "testing started / finished" prose for ONE run, fetched only by the run detail page. Kept off
    // /api/test-sessions deliberately: that payload also feeds the chat agent's per-turn context, where
    // per-session prose would be pure prompt bloat. Either key may be omitted — a run resolved to a
    // session passes both (the 'started' notice is stored against the version, before the session
    // existed), a `github-pr-<versionId>` route with no session yet passes only the version.
    getTestRunNotices: builder.query<
      { notices: TestRunNotice[] },
      { projectId: string; sessionId?: string | null; versionId?: string | null }
    >({
      query: ({ projectId, sessionId, versionId }) => ({
        url: '/api/test-run-notices',
        params: {
          project_id: projectId,
          ...(sessionId ? { session_id: sessionId } : {}),
          ...(versionId ? { version_id: versionId } : {}),
        },
      }),
      transformResponse: (response: unknown) => {
        const data = TestRunNoticesResponseSchema.parse(response)
        return { notices: data.notices }
      },
      // Tagged with the rest of a project's run data so completing a round (which writes the 'finished'
      // notice) refreshes this alongside the session it belongs to.
      providesTags: (_result, _error, arg) => [{ type: 'ProjectTestSessions', id: arg.projectId }],
    }),

    getProjectJourneys: builder.infiniteQuery<
      PaginatedData<Journey>,
      { projectId: string; parentId: string | null; pageSize?: number },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => nextPage(lastPage),
        refetchCachedPages: false,
      },
      query: ({ queryArg, pageParam }) => ({
        url: '/api/journeys',
        params: {
          project_id: queryArg.projectId,
          page: pageParam,
          pageSize: queryArg.pageSize ?? PROJECT_LIST_PAGE_SIZE,
          parent_id: queryArg.parentId === null ? 'none' : queryArg.parentId,
        },
      }),
      transformResponse: parsePaginatedJourneys,
      providesTags: (_result, _error, arg) => [{ type: 'ProjectJourneys', id: arg.projectId }],
    }),

    getJourney: builder.query<Journey | null, string>({
      query: (journeyId) => ({ url: `/api/journeys/${encodeURIComponent(journeyId)}` }),
      transformResponse: (response) => JourneySchema.parse(response) as Journey,
      providesTags: (_result, _error, journeyId) => [{ type: 'Journey', id: journeyId }],
    }),

    reorderJourneyQueue: builder.mutation<
      { moved: { task_id: string; priority: number; queue_rank: string } },
      { projectId: string; taskId: string; beforeTaskId: string | null; afterTaskId: string | null }
    >({
      query: ({ projectId, taskId, beforeTaskId, afterTaskId }) => ({
        url: '/api/journey-queue',
        method: 'PATCH',
        body: {
          action: 'move',
          project_id: projectId,
          task_id: taskId,
          before_task_id: beforeTaskId,
          after_task_id: afterTaskId,
        },
      }),
      transformResponse: (response: unknown) => ReorderJourneyQueueResponseSchema.parse(response),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectJourneys', id: projectId },
        { type: 'ProjectTestSessions', id: projectId },
      ],
    }),

    runQueuedJourneysNext: builder.mutation<{ affected_task_ids: string[] }, { projectId: string; taskIds: string[] }>({
      query: ({ projectId, taskIds }) => ({
        url: '/api/journey-queue',
        method: 'PATCH',
        body: { action: 'run-next', project_id: projectId, task_ids: taskIds },
      }),
      transformResponse: (response: unknown) => BulkJourneyQueueResponseSchema.parse(response),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectJourneys', id: projectId },
        { type: 'ProjectTestSessions', id: projectId },
      ],
    }),

    skipQueuedJourneys: builder.mutation<{ affected_task_ids: string[] }, { projectId: string; taskIds: string[] }>({
      query: ({ projectId, taskIds }) => ({
        url: '/api/journey-queue',
        method: 'PATCH',
        body: { action: 'skip', project_id: projectId, task_ids: taskIds },
      }),
      transformResponse: (response: unknown) => BulkJourneyQueueResponseSchema.parse(response),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectJourneys', id: projectId },
        { type: 'ProjectTestSessions', id: projectId },
      ],
    }),

    getJourneyBugs: builder.query<JourneyBug[], string>({
      query: (journeyId) => ({ url: `/api/journeys/${encodeURIComponent(journeyId)}/bugs` }),
      transformResponse: (response) => JourneyBugsResponseSchema.parse(response).bugs as JourneyBug[],
      providesTags: (_result, _error, journeyId) => [{ type: 'JourneyBugs', id: journeyId }],
    }),

    getChildJourneys: builder.query<PaginatedData<Journey>, { projectId: string; parentId: string }>({
      query: ({ projectId, parentId }) => ({
        url: '/api/journeys',
        params: { project_id: projectId, parent_id: parentId, page: 1, pageSize: 100 },
      }),
      transformResponse: parsePaginatedJourneys,
      providesTags: (_result, _error, { parentId }) => [{ type: 'Journey', id: `children:${parentId}` }],
    }),

    updateJourney: builder.mutation<Journey, { id: string; data: Record<string, unknown> }>({
      query: ({ id, data }) => ({ url: `/api/journeys/${encodeURIComponent(id)}`, method: 'PATCH', body: data }),
      transformResponse: (response) => JourneySchema.parse(response) as Journey,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Journey', id },
        { type: 'ProjectJourneys', id: 'LIST' },
      ],
    }),

    deleteJourney: builder.mutation<void, { id: string; projectId?: string }>({
      query: ({ id }) => ({ url: `/api/journeys/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { id, projectId }) => [
        { type: 'Journey', id },
        ...(projectId ? [{ type: 'ProjectJourneys' as const, id: projectId }] : []),
      ],
    }),

    getJourneyTestRuns: builder.query<TestRun[], { journeyId: string; pageSize?: number }>({
      query: ({ journeyId, pageSize = 100 }) => ({
        url: '/api/test-runs',
        params: { journey_id: journeyId, page: 1, pageSize },
      }),
      transformResponse: (response) => (PaginatedTestRunsResponseSchema.parse(response) as unknown as PaginatedData<TestRun>).items,
      providesTags: (_result, _error, { journeyId }) => [{ type: 'TestRun', id: `journey:${journeyId}` }],
    }),

    createTestRun: builder.mutation<TestRun, Record<string, unknown>>({
      query: (body) => ({ url: '/api/test-runs', method: 'POST', body }),
      transformResponse: (response) => TestRunSchema.parse(response) as unknown as TestRun,
      invalidatesTags: (_result, _error, body) => [
        { type: 'ProjectTestSessions', id: String(body.project_id ?? '') },
        ...(typeof body.journey_id === 'string' ? [{ type: 'TestRun' as const, id: `journey:${body.journey_id}` }] : []),
      ],
    }),

    getProjectExplorations: builder.query<PaginatedData<Exploration>, { projectId: string; page?: number; pageSize?: number }>({
      query: ({ projectId, page = 1, pageSize = 20 }) => ({
        url: '/api/explorations',
        params: { project_id: projectId, page, pageSize },
      }),
      transformResponse: (response) => PaginatedExplorationsResponseSchema.parse(response) as PaginatedData<Exploration>,
      providesTags: (_result, _error, { projectId }) => [{ type: 'ProjectExplorations', id: projectId }],
    }),

    createExploration: builder.mutation<Exploration, { project_id: string; prompt: string; retrigger?: boolean }>({
      query: (body) => ({ url: '/api/explorations', method: 'POST', body }),
      transformResponse: (response) => ExplorationSchema.parse(response) as Exploration,
      invalidatesTags: (_result, _error, { project_id }) => [{ type: 'ProjectExplorations', id: project_id }],
    }),

    getProjectChatMessages: builder.query<ProjectChatMessage[], string>({
      query: (projectId) => ({ url: '/api/project-chat-messages', params: { project_id: projectId } }),
      transformResponse: (response) => ChatMessagesResponseSchema.parse(response).messages.map((message) => ({
        id: message.id,
        role: message.role,
        content: message.content,
        createTime: message.created_at,
        source: message.source ?? null,
        authorId: message.author_id ?? null,
        authorName: message.author_name ?? null,
      })),
      providesTags: (_result, _error, projectId) => [{ type: 'ProjectChat', id: projectId }],
    }),

    appendProjectChatMessages: builder.mutation<{ ok: boolean; count: number }, { projectId: string; messages: ChatMessageData[] }>({
      query: ({ projectId, messages }) => ({
        url: '/api/project-chat-messages',
        method: 'POST',
        body: {
          project_id: projectId,
          messages: messages.map(({ id, role, content, createTime }) => ({ id, role, content, createTime })),
        },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'ProjectChat', id: projectId }],
    }),

    // These endpoints keep the remaining project surfaces on the same RTK Query boundary while
    // their feature-specific UI is migrated away from the old reducers.
    getProjectBugs: builder.query<PaginatedData<Bug>, { projectId: string; page?: number; pageSize?: number; hideResolved?: boolean; severitySort?: 'asc' | 'desc' | null; status?: string; severity?: string; kind?: string; environment?: string }>({
      query: ({ projectId, page = 1, pageSize = 20, hideResolved, severitySort, status, severity, kind, environment }) => ({
        url: '/api/bugs',
        params: {
          project_id: projectId,
          page,
          pageSize,
          ...(hideResolved ? { hideResolved: 'true' } : {}),
          ...(severitySort ? { severitySort } : {}),
          ...(status && status !== 'all' ? { status } : {}),
          ...(severity && severity !== 'all' ? { severity } : {}),
          ...(kind && kind !== 'all' ? { kind } : {}),
          ...(environment && environment !== 'all' ? { environment } : {}),
          // bug-muetzmgg-4470: a stored 403 was replayed to authorized sessions for minutes —
          // fresh key per fetch so the serving cache can never match (see apiCacheBust).
          cachebust: apiCacheBust(),
        },
      }),
      transformResponse: (response) => PaginatedBugsResponseSchema.parse(response) as unknown as PaginatedData<Bug>,
      providesTags: (_result, _error, { projectId }) => [{ type: 'Project', id: `bugs:${projectId}` }],
    }),

    getBug: builder.query<Bug, string>({
      query: (id) => ({ url: `/api/bugs/${encodeURIComponent(id)}` }),
      transformResponse: (response) => BugSchema.parse(response) as unknown as Bug,
      providesTags: (_result, _error, id) => [{ type: 'Project', id: `bug:${id}` }],
    }),

    updateBugDetails: builder.mutation<Bug, { id: string; data: { title?: string; description?: string | null; reproduction_steps?: string | null; expected_behavior?: string | null; actual_behavior?: string | null } }>({
      query: ({ id, data }) => ({ url: `/api/bugs/${encodeURIComponent(id)}`, method: 'PATCH', body: { action: 'update-details', ...data } }),
      transformResponse: (response) => BugSchema.parse(response) as unknown as Bug,
      invalidatesTags: (result, _error, { id }) => bugMutationTags(result, id),
    }),

    changeBugSeverity: builder.mutation<Bug, { id: string; severity: string }>({
      query: ({ id, severity }) => ({ url: `/api/bugs/${encodeURIComponent(id)}`, method: 'PATCH', body: { action: 'change-severity', severity } }),
      transformResponse: (response) => BugSchema.parse(response) as unknown as Bug,
      invalidatesTags: (result, _error, { id }) => bugMutationTags(result, id),
    }),

    changeBugStatus: builder.mutation<Bug, { id: string; status: string }>({
      query: ({ id, status }) => ({ url: `/api/bugs/${encodeURIComponent(id)}`, method: 'PATCH', body: { action: 'change-status', status } }),
      transformResponse: (response) => BugSchema.parse(response) as unknown as Bug,
      invalidatesTags: (result, _error, { id }) => bugMutationTags(result, id),
    }),

    sendBugToWebhook: builder.mutation<Bug, string>({
      query: (id) => ({ url: `/api/bugs/${encodeURIComponent(id)}`, method: 'PATCH', body: { action: 'send-to-webhook' } }),
      transformResponse: (response) => BugSchema.parse(response) as unknown as Bug,
      invalidatesTags: (result, _error, id) => bugMutationTags(result, id),
    }),

    resolveEvidence: builder.mutation<Bug, string>({
      query: (id) => ({ url: `/api/bugs/${encodeURIComponent(id)}`, method: 'PATCH', body: { action: 'resolve-evidence' } }),
      transformResponse: (response) => BugSchema.parse(response) as unknown as Bug,
      invalidatesTags: (result, _error, id) => bugMutationTags(result, id),
    }),

    deleteBug: builder.mutation<void, { id: string; projectId: string }>({
      query: ({ id }) => ({ url: `/api/bugs/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { id, projectId }) => [
        { type: 'Project', id: `bug:${id}` },
        ...bugProjectTags(projectId),
      ],
    }),

    getBugCounts: builder.query<BugCounts, string>({
      query: (projectId) => ({ url: '/api/bugs/counts', params: { project_id: projectId, cachebust: apiCacheBust() } }),
      transformResponse: (response) => BugCountsSchema.parse(response),
      providesTags: (_result, _error, projectId) => [{ type: 'Project', id: `bug-counts:${projectId}` }],
    }),

    // Every count the Insights page draws, for a whole page of projects in one request. Tagged per
    // project with the same bug-counts tag the single-project endpoint uses, so a bug mutation
    // refreshes Insights too.
    getProjectInsights: builder.query<ProjectInsightCounts[], string[]>({
      query: (projectIds) => ({ url: '/api/insights', params: { project_ids: projectIds.join(',') } }),
      transformResponse: (response) => ProjectInsightsResponseSchema.parse(response).projects as ProjectInsightCounts[],
      providesTags: (_result, _error, projectIds) =>
        projectIds.map((projectId) => ({ type: 'Project' as const, id: `bug-counts:${projectId}` })),
    }),

    getBugBreakdown: builder.query<BugBreakdownRow[], string>({
      query: (projectId) => ({ url: '/api/bugs/breakdown', params: { project_id: projectId, cachebust: apiCacheBust() } }),
      transformResponse: (response) => BugBreakdownSchema.parse(response) as BugBreakdownRow[],
      providesTags: (_result, _error, projectId) => [{ type: 'Project', id: `bug-breakdown:${projectId}` }],
    }),

    getComments: builder.query<Comment[], { projectId: string; targetType: CommentTargetType; targetId: string }>({
      query: ({ projectId, targetType, targetId }) => ({
        url: '/api/comments',
        params: { project_id: projectId, target_type: targetType, target_id: targetId },
      }),
      transformResponse: (response) => CommentsResponseSchema.parse(response).items as unknown as Comment[],
      providesTags: (_result, _error, { targetType, targetId }) => [{ type: 'Comments', id: `${targetType}:${targetId}` }],
    }),

    addComment: builder.mutation<Comment, { projectId: string; targetType: CommentTargetType; targetId: string; content: string; mentionedUserIds?: string[] }>({
      query: ({ projectId, targetType, targetId, content, mentionedUserIds }) => ({
        url: '/api/comments',
        method: 'POST',
        body: {
          project_id: projectId,
          target_type: targetType,
          target_id: targetId,
          content,
          mentioned_user_ids: mentionedUserIds ?? [],
        },
      }),
      transformResponse: (response) => CommentSchema.parse(response) as unknown as Comment,
      invalidatesTags: (_result, _error, { targetType, targetId }) => [{ type: 'Comments', id: `${targetType}:${targetId}` }],
    }),

    getNotifications: builder.query<{ items: AppNotification[]; unread: number }, void>({
      query: () => ({ url: '/api/notifications' }),
      transformResponse: (response) => NotificationsResponseSchema.parse(response) as unknown as { items: AppNotification[]; unread: number },
      providesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),

    markNotificationsRead: builder.mutation<{ items: AppNotification[]; unread: number }, { ids?: string[]; all?: boolean }>({
      query: (body) => ({ url: '/api/notifications', method: 'POST', body }),
      transformResponse: (response) => NotificationsResponseSchema.parse(response) as unknown as { items: AppNotification[]; unread: number },
      invalidatesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),

    clearAllNotifications: builder.mutation<{ items: AppNotification[]; unread: number }, void>({
      query: () => ({ url: '/api/notifications', method: 'DELETE' }),
      transformResponse: (response) => NotificationsResponseSchema.parse(response) as unknown as { items: AppNotification[]; unread: number },
      invalidatesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),

    getProcessingSummary: builder.query<{ running: Task[]; queued: Task[]; max_containers: number }, string | undefined>({
      query: (projectId) => ({ url: '/api/tasks/summary', ...(projectId ? { params: { project_id: projectId } } : {}) }),
      transformResponse: (response) => ProcessingSummarySchema.parse(response) as { running: Task[]; queued: Task[]; max_containers: number },
      providesTags: (_result, _error, projectId) => [{ type: 'Project', id: `processing:${projectId ?? 'all'}` }],
    }),

    getProjectTaskActivity: builder.query<ProjectTaskActivity, string>({
      query: (projectId) => ({ url: '/api/tasks/activity', params: { project_id: projectId } }),
      transformResponse: (response) => ProjectTaskActivitySchema.parse(response) as ProjectTaskActivity,
      providesTags: (_result, _error, projectId) => [{ type: 'Project', id: `activity:${projectId}` }],
    }),

    getPlaywrightAnalysis: builder.query<PlaywrightAnalyses['analyses'][number], { projectId: string; analysisId: string }>({
      query: ({ projectId, analysisId }) => ({ url: `/api/v1/projects/${encodeURIComponent(projectId)}/failure-analyses/${encodeURIComponent(analysisId)}` }),
      transformResponse: response => PlaywrightAnalysisSchema.parse(response),
    }),
    getPlaywrightAnalyses: builder.query<PlaywrightAnalyses, { projectId: string; offset?: number; limit?: number }>({
      query: ({ projectId, offset = 0, limit = 20 }) => ({ url: `/api/v1/projects/${encodeURIComponent(projectId)}/failure-analyses`, params: { offset, limit } }),
      transformResponse: (response) => PlaywrightAnalysesSchema.parse(response),
      providesTags: (_result, _error, { projectId }) => [{ type: 'Project', id: `playwright:${projectId}` }],
    }),

    getProjectUserSessions: builder.query<{ sessions: ProjectUserSession[] }, string>({
      query: (projectId) => ({ url: '/api/project-user-sessions', params: { project_id: projectId } }),
      transformResponse: (response) => ProjectUserSessionsResponseSchema.parse(response),
      providesTags: (_result, _error, projectId) => [{ type: 'ProjectUserSessions', id: projectId }],
    }),

    getSessionScreenshot: builder.query<SessionScreenshotResponse, { runId: string; observationId: string }>({
      query: ({ runId, observationId }) => ({ url: '/api/user-session-screenshot', params: { run_id: runId, observation_id: observationId } }),
      transformResponse: (response) => SessionScreenshotResponseSchema.parse(response),
    }),

    getProjectUserSessionDetail: builder.query<ProjectUserSessionDetail, { projectId: string; sessionId: string; journeyId?: string }>({
      async queryFn({ projectId, sessionId, journeyId }, _api, _options, fetchWithBQ) {
        const url = '/api/project-user-sessions'
        const params = { project_id: projectId, session_id: sessionId, ...(journeyId ? { journey_id: journeyId } : {}) }
        const first = await fetchWithBQ({ url, params })
        if (first.error) return { error: first.error }
        try {
          const data = await loadSessionDetail(first.data, async (part, sha256) => {
            const response = await fetchWithBQ({ url, params: { ...params, response_part: part, response_sha256: sha256 } })
            if (response.error) throw new Error(`Could not load session analysis (HTTP ${response.error.status})`)
            return response.data
          })
          return { data }
        } catch (error) {
          return { error: { status: 'CUSTOM_ERROR', error: error instanceof Error ? error.message : 'Could not load session analysis' } }
        }
      },
      providesTags: (_result, _error, { sessionId }) => [{ type: 'ProjectUserSessions', id: `detail:${sessionId}` }],
    }),

    hasProjectUserSessions: builder.query<{ has_sessions: boolean }, string>({
      query: (projectId) => ({ url: '/api/project-user-sessions', params: { project_id: projectId, summary: '1' } }),
      transformResponse: (response) => ProjectUserSessionsPresenceResponseSchema.parse(response),
      providesTags: (_result, _error, projectId) => [{ type: 'ProjectUserSessions', id: projectId }],
    }),

    createProjectUserSessionJourney: builder.mutation<
      { status?: string; started?: boolean; journey?: { id: string } | null },
      { projectId: string; sessionId: string; regenerate?: boolean }
    >({
      query: ({ projectId, sessionId, regenerate }) => ({
        url: '/api/project-user-sessions',
        method: 'POST',
        params: { project_id: projectId },
        body: { action: regenerate ? 'regenerate-journey' : 'create-journey', session_id: sessionId },
      }),
      transformResponse: (response) => CreateProjectUserSessionJourneyResponseSchema.parse(response),
      invalidatesTags: (_result, _error, { projectId, sessionId }) => [
        { type: 'ProjectUserSessions', id: projectId },
        { type: 'ProjectUserSessions', id: `detail:${sessionId}` },
      ],
    }),

    getTaskHistory: builder.query<TaskHistory, { projectId: string; page?: number }>({
      query: ({ projectId, page = 1 }) => ({ url: '/api/tasks/history', params: { project_id: projectId, page, pageSize: 50 } }),
      transformResponse: (response) => TaskHistorySchema.parse(response) as TaskHistory,
    }),

    getProjectSecurityResults: builder.query<ProjectSecurityResults, { projectId: string; includeRequestDetails?: boolean }>({
      // Omitted and explicitly-false includeRequestDetails describe the same request and must share
      // its cache, so the overview security card and the security tab reuse one response instead of
      // re-fetching identical data from each other's mount.
      serializeQueryArgs: ({ queryArgs }) => ({
        projectId: queryArgs.projectId,
        includeRequestDetails: queryArgs.includeRequestDetails === true,
      }),
      query: ({ projectId, includeRequestDetails = false }) => ({ url: '/api/project-security', params: { project_id: projectId, ...(includeRequestDetails ? { include_request_details: 'true' } : {}) } }),
      transformResponse: (response) => ProjectSecurityResultsSchema.parse(response) as ProjectSecurityResults,
      providesTags: (_result, _error, { projectId }) => [{ type: 'Project', id: `security:${projectId}` }],
    }),

  }),
})

export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useGetProjectAccessQuery,
  useAnswerAccessRequestMutation,
  useGetSecurityOwnershipQuery,
  useVerifyProjectOwnershipMutation,
  useGetProjectEnvironmentsQuery,
  useDeleteProjectEnvironmentMutation,
  useUpdateProjectEnvironmentMutation,
  useGetInviteInfoQuery,
  useUpdateProjectMutation,
  useTestIssueTrackerMutation,
  useTestSummaryDestinationMutation,
  useSendCompletionEmailMutation,
  useCreateProjectMutation,
  useStartProjectQaMutation,
  useDeleteProjectMutation,
  useGetReverseProxyStatusQuery,
  useGetReverseProxyEventsQuery,
  useDeleteReverseProxyMutation,
  useGetProjectMembersQuery,
  useGetGithubAppStatusQuery,
  useGetGithubAppInstallationQuery,
  useGetTeamsQuery,
  useRenameTeamMutation,
  useUpdateTeamInviteLinkMutation,
  useGetTeamMembersQuery,
  useInviteTeamMemberMutation,
  useUpdateTeamMemberRoleMutation,
  useRemoveTeamMemberMutation,
  useGetTeamInviteInfoQuery,
  useJoinTeamByInviteMutation,
  useGetProjectPullRequestQuery,
  useGetPullRequestChecksQuery,
  useGetProjectVersionsQuery,
  useGetVersionQuery,
  useGetUsageQuery,
  useGetDailyCreditUsageQuery,
  useGetCreditUsageDayQuery,
  useGetCreditPacksQuery,
  useStartCreditTopUpMutation,
  useGetStripeSubscriptionQuery,
  useGetStripePlansQuery,
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
  useGetProjectUsageQuery,
  useGetApiTokensQuery,
  useGenerateApiTokenMutation,
  useDeleteApiTokenMutation,
  useGetProjectTestSessionsInfiniteQuery,
  useGetProjectTestSessionQuery,
  useGetTestRunNoticesQuery,
  useGetProjectJourneysInfiniteQuery,
  useGetJourneyQuery,
  useGetJourneyBugsQuery,
  useGetChildJourneysQuery,
  useReorderJourneyQueueMutation,
  useRunQueuedJourneysNextMutation,
  useSkipQueuedJourneysMutation,
  useDeleteJourneyMutation,
  useGetJourneyTestRunsQuery,
  useCreateTestRunMutation,
  useGetProjectExplorationsQuery,
  useCreateExplorationMutation,
  useGetProjectChatMessagesQuery,
  useAppendProjectChatMessagesMutation,
  useGetProjectBugsQuery,
  useLazyGetProjectBugsQuery,
  useGetBugQuery,
  useUpdateBugDetailsMutation,
  useChangeBugSeverityMutation,
  useChangeBugStatusMutation,
  useSendBugToWebhookMutation,
  useResolveEvidenceMutation,
  useDeleteBugMutation,
  useGetBugCountsQuery,
  useGetProjectInsightsQuery,
  useGetBugBreakdownQuery,
  useGetCommentsQuery,
  useAddCommentMutation,
  useGetNotificationsQuery,
  useMarkNotificationsReadMutation,
  useClearAllNotificationsMutation,
  useGetProcessingSummaryQuery,
  useGetProjectTaskActivityQuery,
  useGetPlaywrightAnalysisQuery,
  useGetPlaywrightAnalysesQuery,
  useGetProjectUserSessionsQuery,
  useGetProjectUserSessionDetailQuery,
  useGetSessionScreenshotQuery,
  useHasProjectUserSessionsQuery,
  useCreateProjectUserSessionJourneyMutation,
  useGetTaskHistoryQuery,
  useGetProjectSecurityResultsQuery,
} = projectDataApi

export function usePrefetchProjectData(): (projectId: string) => void {
  const prefetchProject = projectDataApi.usePrefetch('getProject', { ifOlderThan: 30 })
  const prefetchMembers = projectDataApi.usePrefetch('getProjectMembers', { ifOlderThan: 30 })
  return useCallback((projectId: string) => {
    prefetchProject(projectId)
    prefetchMembers(projectId)
  }, [prefetchMembers, prefetchProject])
}

export const { useGetProjectSightmapQuery, useGetAppArtifactsQuery, useLazyGetAppArtifactsQuery, useGetAppToolsQuery } = projectDataApi
